/**
 * Persisted "hide empty sections" preference for the shared list's Sections view.
 *
 * A per-user, per-browser, PER-LIST view preference (localStorage) — the same
 * place the arche keeps its other UI prefs (see collapsedSections). When on, the
 * Sections view hides any section whose subtree has no VISIBLE tasks (after the
 * active assignee/review/search filter) — handy when filtering to "Mine" on a big
 * list, while leaving the full structure visible by default for people managing
 * the list.
 *
 * Shape: we store the set of list ids for which hide-empty is ON. A list the user
 * has never toggled is absent → off (the default). Mirrors collapsedSections so
 * every reader stays in sync with one localStorage write per change.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'todo-lists:hide-empty-sections:v1';

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readFromStorage(): Set<string> {
  const storage = safeLocalStorage();
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeToStorage(ids: Set<string>): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // quota / private-mode — persistence degrades to in-session only
  }
}

let snapshot: Set<string> | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): Set<string> {
  if (snapshot === null) snapshot = readFromStorage();
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function commit(next: Set<string>): void {
  snapshot = next;
  writeToStorage(next);
  listeners.forEach((l) => l());
}

export interface HideEmptyHandle {
  /** True when empty sections are hidden for this list. */
  hideEmpty: boolean;
  /** Flip the preference for this list. */
  toggle: () => void;
  /**
   * Force the preference off for this list (no-op if already off). Called when
   * a new (empty) section is created so it isn't immediately hidden — the user
   * sees their new section instead of wondering whether the create failed.
   */
  disable: () => void;
}

/** Subscribe a component to the persisted hide-empty preference for one list. */
export function useHideEmptySections(listId: string): HideEmptyHandle {
  const ids = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const hideEmpty = ids.has(listId);
  const toggle = useCallback(() => {
    const current = getSnapshot();
    const next = new Set(current);
    if (current.has(listId)) next.delete(listId);
    else next.add(listId);
    commit(next);
  }, [listId]);
  const disable = useCallback(() => {
    const current = getSnapshot();
    if (!current.has(listId)) return;
    const next = new Set(current);
    next.delete(listId);
    commit(next);
  }, [listId]);
  return { hideEmpty, toggle, disable };
}
